function path(root, ...params) {
  return `${root}${params}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_APP = '/admin';

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  register: path(ROOTS_AUTH, '/register'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  verify: path(ROOTS_AUTH, '/verify'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page404: '/404',
  page500: '/500',
  // themeShowcase: '/theme-showcase',
  mobeatPlatformGuide: '/mobeat-platform-guide',
  requestDemo: '/request-demo',
};

export const PATH_APP = {
  root: ROOTS_APP,
  admin: {
    overview: path(ROOTS_APP, '/overview'),
    account: path(ROOTS_APP, '/account'),
    profile: path(ROOTS_APP, '/profile'),
    settings: path(ROOTS_APP, '/settings'),
    users: {
      root: path(ROOTS_APP, '/users'),
      newUser: path(ROOTS_APP, '/users/create'),
    },
    products: path(ROOTS_APP, '/products'),
    applications: path(ROOTS_APP, '/applications'),
    billing: path(ROOTS_APP, '/billing'),
    themeShowcase: path(ROOTS_APP, '/theme-showcase'),
    consumptionRequestsGuide: path(ROOTS_APP, '/consumption-requests-guide'),
    applicationOnboarding: path(ROOTS_APP, '/applications/onboarding'),
    demoLeads: path(ROOTS_APP, '/demo-leads'),
    demoLead: (leadId) => path(ROOTS_APP, `/demo-leads/${leadId}`),
  },
  application: {
    overview: (appId) => path('/applications/', `${appId}/overview`),
    topSpenders: (appId) => path('/applications', `/${appId}/top-spenders`),
    user: {
      list: (appId) => path('/applications', `/${appId}/users`),
      detail: (appId, userId) => path('/applications', `/${appId}/users/${userId}`),
    },
    transaction: {
      root: (appId) => path('/applications', `/${appId}/transactions`),
      list: (appId) => path('/applications', `/${appId}/transactions/list`),
      detail: (appId, transactionId) => path('/applications', `/${appId}/transactions/${transactionId}`),
      appStoreLookup: (appId) => path('/applications', `/${appId}/transactions/app-store-lookup`),
    },
    consumptionRequest: {
      root: (appId) => path('/applications', `/${appId}/consumption-requests`),
      list: (appId) => path('/applications', `/${appId}/consumption-requests/list`),
      detail: (appId, requestId) => path('/applications', `/${appId}/consumption-requests/${requestId}`),
      approvedTransactions: (appId) => path('/applications', `/${appId}/consumption-requests/approved-transactions`),
    },
    chart: {
      explore: (appId) => path('/applications', `/${appId}/charts/explore`),
      revenue: (appId) => path('/applications', `/${appId}/charts/revenue`),
      LTVPerPayingUsers: (appId) => path('/applications', `/${appId}/charts/ltv-per-paying-users`),
      LTVPerAllUsers: (appId) => path('/applications', `/${appId}/charts/ltv-per-all-users`),
      mrr: (appId) => path('/applications', `/${appId}/charts/mrr`),
      arr: (appId) => path('/applications', `/${appId}/charts/arr`),
      churn: (appId) => path('/applications', `/${appId}/charts/churn`),
      refundRate: (appId) => path('/applications', `/${appId}/charts/refund-rate`),
      appStoreRefundRequests: (appId) => path('/applications', `/${appId}/charts/app-store-refund-requests`),
      playStoreCancelReasons: (appId) => path('/applications', `/${appId}/charts/play-store-cancel-reasons`),
      cancellationTiming: (appId) => path('/applications', `/${appId}/charts/cancellation-timing`),
      subscriptionStatus: (appId) => path('/applications', `/${appId}/charts/subscription-status`),
      conversionToPaying: (appId) => path('/applications', `/${appId}/charts/conversion-to-paying`),
      initialConversion: (appId) => path('/applications', `/${appId}/charts/initial-conversion`),
      activeSubscribers: (appId) => path('/applications', `/${appId}/charts/active-subscribers`),
      trialConversion: (appId) => path('/applications', `/${appId}/charts/trial-conversion`),
      funnel: (appId) => path('/applications', `/${appId}/charts/funnel`),
      offerConversion: (appId) => path('/applications', `/${appId}/charts/offer-conversion`),
      reactivation: (appId) => path('/applications', `/${appId}/charts/reactivation`),
      activeTrials: (appId) => path('/applications', `/${appId}/charts/active-trials`),
      newTrials: (appId) => path('/applications', `/${appId}/charts/new-trials`),
      activeTrialsMovement: (appId) => path('/applications', `/${appId}/charts/active-trials-movement`),
      activeSubscriptionsMovement: (appId) => path('/applications', `/${appId}/charts/active-subscriptions-movement`),
      cohortExplorer: (appId) => path('/applications', `/${appId}/charts/cohort-explorer`),
      cohortExplorerRecoup: (appId) => path('/applications', `/${appId}/charts/cohort-explorer-recoup`),
      ltvPrediction: (appId) => path('/applications', `/${appId}/charts/ltv-prediction`),
      cohortedInitialConversion: (appId) => path('/applications', `/${appId}/charts/cohorted-initial-conversion`),
      cohortedTrialConversion: (appId) => path('/applications', `/${appId}/charts/cohorted-trial-conversion`),
      cohortedIntroOfferConversion: (appId) =>
        path('/applications', `/${appId}/charts/cohorted-intro-offer-conversion`),
      cohortedConversionToPaying: (appId) => path('/applications', `/${appId}/charts/cohorted-conversion-to-paying`),
      cohortedReactivation: (appId) => path('/applications', `/${appId}/charts/cohorted-reactivation`),
      autoRenewStatus: (appId) => path('/applications', `/${appId}/charts/auto-renew-status`),
      subscriptionRetention: (appId) => path('/applications', `/${appId}/charts/subscription-retention`),
      subscriptionEvents: (appId) => path('/applications', `/${appId}/charts/subscription-events`),
      adROI: (appId) => path('/applications', `/${appId}/charts/ad-roi`),
      trafficQuality: (appId) => path('/applications', `/${appId}/charts/traffic-quality`),
      costETL: (appId) => path('/applications', `/${appId}/charts/cost-etl`),
    },
    mailingNotifications: {
      userList: {
        list: (appId) => path('/applications', `/${appId}/mailing-notifications/user-lists`),
        new: (appId) => path('/applications', `/${appId}/mailing-notifications/user-lists/new`),
        edit: (appId, listId) => path('/applications', `/${appId}/mailing-notifications/user-lists/${listId}/edit`),
        detail: (appId, listId) => path('/applications', `/${appId}/mailing-notifications/user-lists/${listId}`),
      },
      pushTemplate: {
        list: (appId) => path('/applications', `/${appId}/mailing-notifications/push-templates`),
        new: (appId) => path('/applications', `/${appId}/mailing-notifications/push-templates/new`),
        edit: (appId, templateId) =>
          path('/applications', `/${appId}/mailing-notifications/push-templates/${templateId}`),
      },
      campaign: {
        list: (appId) => path('/applications', `/${appId}/mailing-notifications/campaigns`),
        new: (appId) => path('/applications', `/${appId}/mailing-notifications/campaigns/new`),
        edit: (appId, campaignId) => path('/applications', `/${appId}/mailing-notifications/campaigns/${campaignId}`),
        report: (appId, campaignId) =>
          path('/applications', `/${appId}/mailing-notifications/campaigns/${campaignId}/report`),
      },
      lifecycle: (appId) => path('/applications', `/${appId}/mailing-notifications/lifecycle`),
      lifecycleFlow: {
        view: (appId, flowId) => path('/applications', `/${appId}/mailing-notifications/lifecycle/${flowId}`),
        results: (appId, flowId) =>
          path('/applications', `/${appId}/mailing-notifications/lifecycle/${flowId}/results`),
        newStep: (appId, flowId) =>
          path('/applications', `/${appId}/mailing-notifications/lifecycle/${flowId}/steps/new`),
        editStep: (appId, flowId, stepId) =>
          path('/applications', `/${appId}/mailing-notifications/lifecycle/${flowId}/steps/${stepId}`),
        merge: (appId, flowId, stepIds) =>
          path('/applications', `/${appId}/mailing-notifications/lifecycle/${flowId}/merge?steps=${stepIds.join(',')}`),
      },
      lists: (appId) => path('/applications', `/${appId}/mailing-notifications/lists`),
      mailing: {
        list: (appId) => path('/applications', `/${appId}/mailing-notifications/mailings`),
        new: (appId) => path('/applications', `/${appId}/mailing-notifications/mailings/new`),
        edit: (appId, mailingId) => path('/applications', `/${appId}/mailing-notifications/mailings/${mailingId}/edit`),
      },
    },
    product: {
      list: (appId) => path('/applications', `/${appId}/products`),
      new: (appId) => path('/applications', `/${appId}/products/new`),
      prices: (appId) => path('/applications', `/${appId}/products/prices`),
    },
    mailing: {
      list: (appId) => path('/applications', `/${appId}/mailing`),
      new: (appId) => path('/applications', `/${appId}/mailing/new`),
    },
    webhook: {
      list: (appId) => path('/applications', `/${appId}/webhooks`),
      new: (appId) => path('/applications', `/${appId}/webhooks/new`),
      deliveries: (appId, webhookId) => path('/applications', `/${appId}/webhooks/${webhookId}/deliveries`),
    },
    abTest: {
      report: (appId) => path('/applications', `/${appId}/ab-tests`),
    },
    settings: (appId) => path('/applications', `/${appId}/settings`),
    retentionMessaging: (appId) => path('/applications', `/${appId}/retention-messaging`),
  },
};
